/**
 * This enum should contain a message keys for all messages between panels.
 *
 * Message keys must be unique because multiple panels can receive the same message.
 */
export default {

    /**
     * This message is fired to any panel that is initialized without another message.
     */
    DEFAULT: "DEFAULT",

    /**
     * This message is fired when a panel should be initialized.
     */
    FOCUS: "FOCUS",

    /**
     * This message is fired to panels when the context of the workspace is being changed.
     * This can happen for two reasons: The user has selected a new workspace or the user has returned a job.
     */
    WORKSPACE_CHANGE: "WORKSPACE_CHANGE",

    /**
     * This message is fired to panels when a panel has been opened or closed.
     *
     * Currently only sent to viewer and contains open/close state of indexing panel.
     * Can be expanded as necessary.
     */
    PANEL_CHANGE: "PANEL_CHANGE",

    /**
     * This message should almost always be completely ignored.
     * 
     * It should be fired when a panel wants to force another panel open immediately without all the info needed to send the correct initialization call.
     */
    PRELOAD: "PRELOAD",

    /**
     * This message signifies that a panel is closing.
     *
     */
    CLOSE: "CLOSE",

    /**
     * Load a panel from its serialized state.
     */
    UNSERIALIZE: "UNSERIALIZE",

    /**
     * Payload of { execute, uid }:
     *
     * - execute: search encapsulated business logic data
     * - uid: unique identifier for search execution
     */
    EXECUTE_SEARCH: "EXECUTE_SEARCH",

    /**
     * Payload of { execute, uid }, see EXECUTE_SEARCH. uid is the same if prompts haven't changed, only filter.
     */
    FILTER_SEARCH: "FILTER_SEARCH",

    /**
     * Payload of {execute, uid, prompts, searchId, isExecuted}.
     * -execute: search encapsulated business logic data
     * -uid: unique identifier for search execution
     * -prompts: A preloaded prompts datasource.  Should not requiring any processing.
     * -isExecuted: Was a EXECUTE_SEARCH called for this message.
     * searchId: ID of search
     */
    PROMPT_SEARCH: "PROMPT_SEARCH",

    /**
     * Payload of { uid }:
     *
     * - uid: the uid of the search results tab / content to filter from. Will only filter if this matches the search inputs currently shown.
     */
    FILTER_REFRESH: "FILTER_REFRESH",

    /**
     * Signify that a panel/list has updated state.
     * Used to control next/prev document in the viewer.
     * Payload of {uid, panel};
     * -uid Uid of document list. Optional. If not provided, assumes all for that list are gone
     * -panel Panel that was displaying the list
     */
    DOCUMENT_LIST_GONE: "DOCUMENT_LIST_GONE",

    /**
     * Move to next document.
     *
     * Will be passed docId and uid of document list.
     *
     */
    NEXT_DOCUMENT: "NEXT_DOCUMENT",

    /**
     * Move to previous document.
     *
     * Will be passed docId and uid of document list.
     */
    PREVIOUS_DOCUMENT: "PREVIOUS_DOCUMENT",

    /**
     * Execute a job view.
     *
     * - execute: job view encapsulated business logic data.
     * - viewId: job view ID
     */
    EXECUTE_JOB_VIEW: "EXECUTE_JOB_VIEW",

    /**
     * Show a particular job.
     *
     * -execute: job view encapsulated business logic data.
     * -jobId: ID of job to display
     */
    SHOW_JOB: "SHOW_JOB",

    /**
     * Return the specified job.
     *
     * -dispositionQueueId: disposition queue id.
     */
    RETURN_JOB: "RETURN_JOB",

    /**
     * Mark the specified task complete.
     *
     * -taskId: ID of the task to mark complete.
     */
    RM_TASK_COMPLETE: "RM_TASK_COMPLETE",

    /**
     * Mark a task or job as cancelled; typical during lazy feature rights checks.
     *
     * -dispositionQueueId: disposition queue id.
     */
    RM_TASK_CANCEL: "RM_TASK_CANCEL",

    /**
     * Show a particular job view.
     *
     * -execute: job view encapsulated business logic data.
     * -viewId: job view id
     */
    SHOW_VIEW: "SHOW_VIEW",

    /**
     * Update a particular job view with a specific job count.
     *
     * -total: The number of jobs
     * -viewId: The job view
     */
    UPDATE_VIEW: "UPDATE_VIEW",

    /**
     * Payload is an array of user setting names that were changed.
     */
    USER_SETTING_CHANGED: "app.userSettingChanged",

    /**
     * Minimize the target panel.
     */
    MINIMIZE: "MINIMIZE",

    /**
     * Set the document for a panel, causing it to update.
     *
     * - docId: document ID
     * - fileDefinitionType: file definition type
     * - sourceType: one of EditSourceType enum.  Used so that the document can be edited correctly
     * - categoryId
     * - documentTypeId
     */
    SET_DOCUMENT: "SET_DOCUMENT",

    /**
     * Variant of SET_DOCUMENT only fired by scanning.
     *
     * This must be different than SET_DOCUMENT so that locking can be handled differently.
     */
    SET_SCANNED_DOCUMENT: "SET_SCANNED_DOCUMENT",

    /**
     * View a retention policy.  Will be passed the id of a retention policy.
     */
    VIEW_RETENTION: "VIEW_RETENTION",

    /**
     * View a legal case.  Will be passed the id of a legal case.
     */
    VIEW_LEGAL_CASE: "VIEW_LEGAL_CASE",

    /**
     * View the specified disposition queue.
     */
    VIEW_DISPOSITION_QUEUE: "VIEW_DISPOSITION_QUEUE",

    /**
     * View a process instance.  Will be passed the id of the process instance.
     */
    VIEW_PROCESS_INSTANCE: "VIEW_PROCESS_INSTANCE",

    /**
     * View an export.  Will be passed the id of the export job.
     */
    VIEW_EXPORT: "VIEW_EXPORT",

    /**
     * View a user system message. Will be passed the id of the system message.
     */
    VIEW_MESSAGE: "VIEW_MESSAGE",

    /**
     * Open the editing queue.  Can be passed with an optional edit lock ID.
     */
    OPEN_EDITING: "OPEN_EDITING",

    /**
     * Open the scanning queue.
     */
    OPEN_SCANNING: "OPEN_SCANNING",

    /**
     * This message will be sent to panels to inform them when documents have had their edit state or existence changed.
     *
     * Will be passed an array of document ids.
     */
    DOCUMENTS_CHANGED: "DOCUMENTS_CHANGED",

    /**
     * When the folder changed, i.e. documents removed or deleted.
     * Will be passed an array of folder ids.
     */
    FOLDERS_CHANGED: "FOLDERS_CHANGED",

    /**
     * Signal changes to editing. Passed an object with 3 parameters
     * -docsRemoved: doc ids of things removed
     * -locksAdded: edit lock ids of things added
     * -locksRemoved: edit lock ids of things removed
     */
    ADJUST_EDITING: "ADJUST_EDITING",

    /**
     * Open the forms panel and load a form to be shown in view mode.
     * Passed object must be a SavedFormModel, and include EITHER formName, formId, or id (which is a form instance id).
     */
    VIEW_FORM: "VIEW_FORM",

    /**
     * Open the forms panel and load a form to be shown in edit mode.
     * Passed object must be a SavedFormModel, and include EITHER formName, formId, or id (which is a form instance id).
     */
    EDIT_FORM: "EDIT_FORM",

    /**
     * Open the Batches panel and check out the given batch.  Passed object may include batchId.
     * "myuploads" is a valid batch ID.
     */
    CHECK_OUT_BATCH: "CHECK_OUT_BATCH",

    /**
     * Open the batches panel and open file upload dialog.  Passed object may include documentTypeId and metadata values.
     */
    FILE_UPLOAD: "FILE_UPLOAD",

    /**
     * Tell the batch panel to create a pointer.  Payload contains docId.
     */
    CREATE_POINTER: "CREATE_POINTER",

    /**
     * Tell the batch panel to check in its current batch.
     */
    CHECK_IN_BATCH: "CHECK_IN_BATCH",

    /**
     * Tell the batch panel to commit its current batch.
     */
    COMMIT_BATCH: "COMMIT_BATCH",

    /**
     * Denotes that the current batch has been taken away from this user.
     */
    BATCH_TAKEN: "BATCH_TAKEN",

    /**
     * Denotes that the user has tried to save, check in, or commit an invalid batch.
     */
    BATCH_INVALID: "BATCH_INVALID",

    /**
     * Indicates that a batch was checked out.
     */
    BATCH_CHECKED_OUT: "BATCH_CHECKED_OUT",

    /**
     * Indicates the document was removed from the current view, usually because it was deleted, but not always.
     */
    DOCUMENT_REMOVED: "DOCUMENT_REMOVED",

    /**
     * Indicates to search results that the current search is related to a BPM task, and therefore must be run otherwise task is cancelled.
     */
    BPM_TASK_SEARCH: "BPM_TASK_SEARCH",

    /**
     * Indicates to forms that the current form is related to a BPM task, and therefore must be saved/submitted otherwise task is cancelled.
     */
    BPM_TASK: "BPM_TASK",

    /**
     * Indicates to RM that a disposition queue is being returned via Jobs for a Review Records task
     */
    RETURN_DISPOSITION_QUEUE: "RETURN_DISPOSITION_QUEUE",

    /**
     * Delays refreshing a component until a promise is resolved.
     */
    DELAY_REFRESH: "DELAY_REFRESH",

    /**
     * Begin running a form task in Jobs
     */
    RUN_FORM_TASK: "RUN_FORM_TASK",

    /**
     * Set default focus.
     */
    SET_FOCUS: "SET_FOCUS",

    /**
     * When the user adds a folder, this is called. Payload will include name and id of the new folder.
     */
    FOLDER_ADDED: "FOLDER_ADDED",

    /**
     * Select and open a folder to view its contents.
     */
    OPEN_FOLDER: "OPEN_FOLDER",

    /**
     * User has saved a document in the viewer that may have changed redactions.  Payload is object with docId and redacted flag.
     */
    DOCUMENT_REDACTIONS_CHANGED: "DOCUMENT_REDACTIONS_CHANGED"
};
